/**
 * Sprint V101.b · Admin-Webinar-Panel (20.09.2026, Andreas).
 *
 * Ermöglicht Andreas, das Webinar-Datum (und Begleit-Felder) ohne Redeploy
 * direkt in der DB anzupassen. Anlass: Terminverschiebung Di 22.09. → Fr
 * 25.09. Banner + alle E-Mails ziehen den Termin dynamisch aus dem
 * webinar-Dokument, deshalb ist dieses Panel die Single Source of Truth.
 *
 * Auth: X-Admin-Token in localStorage (Muster von AdminBootstrap).
 * Backend: PUT /api/admin/webinar/{id} (bereits vorhanden aus V98).
 */
import React, { useEffect, useState } from "react";
import { Loader2, Shield, Save, Calendar, ExternalLink } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { useAdminToken } from "@/context/AdminTokenContext";

const WEBINAR_ID = "1"; // Stage 1: Single Webinar

const STATUS_OPTIONS = [
  { value: "scheduled", label: "Geplant" },
  { value: "live", label: "Live" },
  { value: "completed", label: "Beendet" },
  { value: "cancelled", label: "Abgesagt" },
];

/** ISO `2026-09-25T10:00:00+02:00` -> `2026-09-25T10:00` für <input type="datetime-local"> */
function isoToLocalInput(iso) {
  if (!iso) return "";
  try {
    // Wir speichern in ISO mit Timezone Europe/Berlin; für die Anzeige im
    // datetime-local-Input schneiden wir die Timezone weg (der Input kennt
    // keine Timezone) und nehmen die lokale Uhrzeit.
    const m = String(iso).match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
    return m ? `${m[1]}T${m[2]}` : "";
  } catch {
    return "";
  }
}

/** `2026-09-25T10:00` -> `2026-09-25T10:00:00+02:00` (Europe/Berlin, CEST/CET-aware) */
function localInputToIso(local) {
  if (!local || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(local)) return "";
  const [datePart, timePart] = local.split("T");
  // Prüfe via Intl, ob das Datum in Europe/Berlin CEST oder CET liegt.
  const dtf = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Berlin",
    timeZoneName: "short",
  });
  const parts = dtf.formatToParts(new Date(`${datePart}T${timePart}:00Z`));
  const tz = (parts.find((p) => p.type === "timeZoneName") || {}).value || "";
  const offset = tz === "CEST" ? "+02:00" : "+01:00";
  return `${datePart}T${timePart}:00${offset}`;
}

/** Vorschau: „Freitag, 25.09.2026 · 10:00 Uhr" */
function previewGerman(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const dtf = new Intl.DateTimeFormat("de-DE", {
      timeZone: "Europe/Berlin",
      weekday: "long",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const parts = dtf.formatToParts(d).reduce(
      (a, x) => ({ ...a, [x.type]: x.value }),
      {},
    );
    return `${parts.weekday}, ${parts.day}.${parts.month}.${parts.year} · ${parts.hour}:${parts.minute} Uhr`;
  } catch {
    return iso;
  }
}

export default function AdminWebinar() {
  const { token } = useAdminToken();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Bearbeitete Felder
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [eventLocal, setEventLocal] = useState("");
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [joinUrl, setJoinUrl] = useState("");
  const [status, setStatus] = useState("scheduled");
  const [bannerEnabled, setBannerEnabled] = useState(true);
  // Sprint V102.h (22.09.2026) — Buchbarkeit + Waitlist-URL.
  const [isBookable, setIsBookable] = useState(true);
  const [waitlistUrl, setWaitlistUrl] = useState("");
  const [registrationCount, setRegistrationCount] = useState(0);
  // Ursprungswerte für Diff/Preview
  const [initial, setInitial] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const r = await api.get(`/webinar/${WEBINAR_ID}`);
        if (cancelled) return;
        const d = r.data || {};
        setTitle(d.title || "");
        setSubtitle(d.subtitle || "");
        setEventLocal(isoToLocalInput(d.event_datetime));
        setDurationMinutes(Number(d.duration_minutes || 60));
        setJoinUrl(d.join_url || "");
        setStatus(d.status || "scheduled");
        setBannerEnabled(d.banner_enabled !== false); // default true
        setIsBookable(d.is_bookable !== false); // default true
        setWaitlistUrl(d.waitlist_url || "");
        setInitial(d);
        // Registrations-Count parallel laden (fuer Warnung bei Deaktivierung)
        try {
          const tokenNow = (token || localStorage.getItem("adminToken") || "").trim();
          if (tokenNow) {
            const cr = await api.get(`/admin/webinar/${WEBINAR_ID}/registrations`, {
              headers: { "X-Admin-Token": tokenNow },
            });
            if (!cancelled) setRegistrationCount(Array.isArray(cr.data) ? cr.data.length : 0);
          }
        } catch (_e) {
          // still ok — Count bleibt 0
        }
      } catch (e) {
        setError("Webinar-Daten konnten nicht geladen werden.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const eventIso = eventLocal ? localInputToIso(eventLocal) : "";
  const previewLine = eventIso ? previewGerman(eventIso) : "—";
  const hasChanges = initial && (
    title !== (initial.title || "") ||
    subtitle !== (initial.subtitle || "") ||
    eventIso !== (initial.event_datetime || "") ||
    Number(durationMinutes) !== Number(initial.duration_minutes || 60) ||
    joinUrl !== (initial.join_url || "") ||
    status !== (initial.status || "scheduled") ||
    bannerEnabled !== (initial.banner_enabled !== false) ||
    isBookable !== (initial.is_bookable !== false) ||
    waitlistUrl !== (initial.waitlist_url || "")
  );

  function handleBookableToggle() {
    const next = !isBookable;
    // Wenn wir auf "nicht buchbar" schalten und es gibt bereits Anmeldungen,
    // vorher hart nachfragen — Andreas' Regel: nie ohne bewusste Entscheidung.
    if (!next && registrationCount > 0) {
      const ok = window.confirm(
        `Achtung: Es sind bereits ${registrationCount} Personen fuer diesen Termin ` +
        `angemeldet. Wenn du die Buchbarkeit deaktivierst, koennen keine neuen ` +
        `Anmeldungen mehr kommen — die bestehenden bleiben erhalten (E-Mail-Reminder ` +
        `laufen weiter). Trotzdem deaktivieren?`,
      );
      if (!ok) return;
    }
    setIsBookable(next);
  }

  async function save(e) {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!token.trim()) { setError("Admin-Token fehlt."); return; }
    if (!eventIso) { setError("Termin ist ungültig."); return; }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        subtitle: subtitle.trim() || null,
        event_datetime: eventIso,
        duration_minutes: Number(durationMinutes),
        join_url: joinUrl.trim(),
        status,
        banner_enabled: bannerEnabled,
        is_bookable: isBookable,
        waitlist_url: waitlistUrl.trim() || null,
      };
      const r = await api.put(`/admin/webinar/${WEBINAR_ID}`, payload, {
        headers: { "X-Admin-Token": token.trim() },
      });
      const d = r.data || {};
      // Re-normalisierte Werte übernehmen
      setInitial({
        title: d.title,
        subtitle: d.subtitle,
        event_datetime: d.event_datetime,
        duration_minutes: d.duration_minutes,
        join_url: d.join_url,
        status: d.status,
        banner_enabled: d.banner_enabled,
        is_bookable: d.is_bookable,
        waitlist_url: d.waitlist_url,
      });
      setSuccess(`Gespeichert · Neuer Termin: ${previewGerman(d.event_datetime)}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : detail?.error || "Speichern fehlgeschlagen (Token korrekt?).",
      );
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="py-16 flex items-center justify-center text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Lade Webinar-Daten …
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="admin-webinar-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-[#F3651D]/10 border border-[#F3651D]/30 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-[#F3651D]" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">Webinar verwalten</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">Termin ändern, Zoom-URL aktualisieren, Status setzen. Änderung wirkt sofort auf Banner + E-Mails.</p>
          </div>
        </div>

        {!token && (
          <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-200 text-sm px-3 py-2 flex items-start gap-2" data-testid="admin-warning">
            <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>Kein Admin-Token gesetzt. Trage ihn in der Sidebar (Fußzeile) ein, sonst schlägt „Speichern" fehl.</span>
          </div>
        )}

        <form onSubmit={save} className="space-y-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 sm:p-7 shadow-sm">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Titel</label>
            <input
              data-testid="admin-webinar-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-white"
              maxLength={120}
            />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Untertitel</label>
            <textarea
              data-testid="admin-webinar-subtitle"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-white"
              maxLength={300}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Termin (Europe/Berlin)</label>
              <input
                data-testid="admin-webinar-datetime"
                type="datetime-local"
                value={eventLocal}
                onChange={(e) => setEventLocal(e.target.value)}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-white"
              />
              <p className="mt-1.5 text-[11px] text-slate-500" data-testid="admin-webinar-preview">
                Anzeige: <span className="font-mono">{previewLine}</span>
              </p>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Dauer (Minuten)</label>
              <input
                data-testid="admin-webinar-duration"
                type="number"
                min={15}
                max={240}
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(e.target.value)}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-white"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Zoom Join-URL</label>
            <input
              data-testid="admin-webinar-join-url"
              value={joinUrl}
              onChange={(e) => setJoinUrl(e.target.value)}
              className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-white font-mono"
              placeholder="https://us06web.zoom.us/j/…"
            />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Status</label>
            <select
              data-testid="admin-webinar-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full sm:w-64 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-white"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Sprint V102.h — Buchbarkeit deaktivieren (Termin bleibt sichtbar) */}
          <div className={`rounded-lg border p-4 ${
            isBookable
              ? "border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950"
              : "border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40"
          }`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className={`text-sm font-bold ${
                  isBookable ? "text-slate-900 dark:text-white" : "text-red-900 dark:text-red-200"
                }`}>
                  Anmeldung buchbar
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Wenn aus, sehen Besucher der Regpage den Termin weiterhin, koennen sich aber nicht anmelden — statt Formular erscheint der Warteliste-Hinweis mit Link zum Fokustypen-Test.
                </div>
                {registrationCount > 0 && (
                  <div className="mt-1.5 text-xs text-amber-800 dark:text-amber-300 font-semibold">
                    Aktuell {registrationCount} bestehende Anmeldung{registrationCount === 1 ? "" : "en"} — bleiben erhalten.
                  </div>
                )}
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={isBookable}
                onClick={handleBookableToggle}
                data-testid="admin-webinar-bookable-toggle"
                className={`relative inline-flex h-7 w-12 flex-shrink-0 rounded-full border-2 border-transparent transition-colors ${
                  isBookable ? "bg-[#F3651D]" : "bg-slate-300 dark:bg-slate-700"
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow ring-0 transition-transform ${
                    isBookable ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
            {!isBookable && (
              <div className="mt-3 pt-3 border-t border-red-200 dark:border-red-800/60">
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
                  Alternativ-Ziel-URL (optional)
                </label>
                <input
                  data-testid="admin-webinar-waitlist-url"
                  value={waitlistUrl}
                  onChange={(e) => setWaitlistUrl(e.target.value)}
                  placeholder="https://… (Default: /quickcheck)"
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-white font-mono"
                />
                <p className="mt-1.5 text-[11px] text-slate-500">
                  Der „Jetzt QuickCheck starten"-Button auf der Regpage fuehrt hierhin. Wenn leer, faellt er auf <code>/quickcheck</code>.
                </p>
              </div>
            )}
          </div>

          {/* Sprint V101.c — Banner ein/aus */}
          <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 px-4 py-3">
            <div>
              <div className="text-sm font-bold text-slate-900 dark:text-white">Free-Dashboard-Banner</div>
              <div className="text-xs text-slate-500 mt-0.5">
                Wenn aus, erscheint das „Live-Webinar"-Banner nicht mehr im Free-Dashboard, in Muster- und Profil-Seiten. Alle anderen Seiten (Regpage, E-Mails) bleiben aktiv.
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={bannerEnabled}
              onClick={() => setBannerEnabled((v) => !v)}
              data-testid="admin-webinar-banner-toggle"
              className={`relative inline-flex h-7 w-12 flex-shrink-0 rounded-full border-2 border-transparent transition-colors ${
                bannerEnabled ? "bg-[#F3651D]" : "bg-slate-300 dark:bg-slate-700"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow ring-0 transition-transform ${
                  bannerEnabled ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
          </div>

          {error && (
            <div data-testid="admin-webinar-error" className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-950/40 text-red-900 dark:text-red-200 text-sm px-3 py-2">
              {error}
            </div>
          )}
          {success && (
            <div data-testid="admin-webinar-success" className="rounded-lg border border-green-300 bg-green-50 dark:bg-green-950/40 text-green-900 dark:text-green-200 text-sm px-3 py-2">
              {success}
            </div>
          )}

          <div className="flex items-center justify-between pt-2">
            <a
              href={`/webinar/${WEBINAR_ID}/anmeldung`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
              data-testid="admin-webinar-open-regpage"
            >
              <ExternalLink className="w-4 h-4" /> Regpage öffnen
            </a>
            <button
              type="submit"
              disabled={saving || !hasChanges}
              data-testid="admin-webinar-save"
              className={`inline-flex items-center gap-2 rounded-lg font-bold px-5 py-2.5 text-sm transition-opacity ${
                hasChanges
                  ? "bg-[#F3651D] hover:opacity-90 text-white"
                  : "bg-slate-200 dark:bg-slate-800 text-slate-500 cursor-not-allowed"
              }`}
            >
              {saving ? (<Loader2 className="w-4 h-4 animate-spin" />) : (<Save className="w-4 h-4" />)}
              {saving ? "Speichern …" : hasChanges ? "Speichern" : "Keine Änderungen"}
            </button>
          </div>
        </form>

        <p className="mt-4 text-xs text-slate-400 leading-relaxed">
          Änderungen wirken sofort auf: Banner im Dashboard („Kostenfreies Live-Webinar"), Regpage, Bestätigungs-Seite, Confirmation-Mail sowie die T-3h- und T-15min-Reminder. Wochentag, Uhrzeit und Tag nach Webinar (Aufzeichnungs-Zustellung) werden aus dem Termin abgeleitet.
        </p>
      </div>
    </AdminLayout>
  );
}
